<template>
    <vx-chart-line
        legend
        tooltipItemKey="label"
        :loading="loading"
        :error="hasError"
        @error="chartError = true"
        :data="dataComputed.data"
        :colors="dataComputed.colors"
        :axis="{
            x: {
                key: 'date',
                axis: true,
                label: '',
                type: 'point',
                align: 0,
                padding: 0,
                ticks: 10,
                interval: 'day',
                tickFormat: (v) => dayjs.utc(v).format('MM/DD'),
            },
            y: {
                key: 'count',
                axis: true,
                label: '',
                ticks: 5,
                tickFormat: (v) => formatCount(v),
            },
        }"
    >
        <template v-slot:title>
            <slot name="title">
                <span> Daily New Contacts, Subscribers, and Unsubscribers </span>
            </slot>
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>
        <template v-slot:right>
            <slot name="right"> </slot>
        </template>
        <template v-slot:footer>
            <vx-button href="/contacts" outline color="muted-light" size="xs">
                Go to Contacts <font-awesome-icon widthAuto :icon="faArrowRight" />
            </vx-button>
        </template>
        <template v-slot:explainHeader>Daily New Contacts, Subscribers and Unsubscribers</template>
        <template v-slot:explainContent>
            <p>Evolution of Contacts, Subscribers and those who have Unsubscribed across time.</p>
            <p>
                <strong>New Contacts</strong><br />
                Gross Increase Count of Contacts during the selected period.
            </p>

            <p>
                <strong>New Subscribers</strong><br />
                Gross Increase Count of Contacts that consented to receive marketing messages, during the selected
                period.
            </p>

            <p>
                <strong>New Usubscribers</strong><br />
                Gross Increase Count of Contacts that have explicitly denied receiving marketing messages, during the
                selected period.
            </p>
        </template>
    </vx-chart-line>
</template>
<script setup>
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxChartLine } from '@voxie/frontend-components';
import { ref, computed, watch } from 'vue';
import { analyticsClient } from '~/services';
import { formatCount } from '~/components/filters';
import { calculateRangeBetweenTwoDates } from '~/utils/analytics';
import dayjs from '~/utils/dayjs';
import { getClientTz } from '~/utils/date';
import { useVersionedRequest } from '~/composables/useVersionedRequest';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    startDate: {
        type: String,
        required: true,
    },
    endDate: {
        type: String,
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    type: {
        type: String,
        default: undefined,
        validator: (type) => ['affiliated', 'preferred'].includes(type),
    },
});

const chartError = ref(false);

const { loading, response, error, fetchData } = useVersionedRequest();

const hasError = computed(() => {
    return Boolean(error.value) || chartError.value;
});

const computedRange = computed(() =>
    calculateRangeBetweenTwoDates({
        startDate: props.startDate,
        endDate: props.endDate,
    })
);

const mapContactsTotalCreated = (rawData) => {
    if (!rawData || !rawData?.values?.length) {
        return [];
    }

    const mappedData = computedRange.value.map((date, index) => {
        const value = rawData.values[index] || 0;
        return {
            label: new Date(date),
            count: value,
            tooltip: `${dayjs.utc(date).tz(getClientTz()).format('MM/DD')}: ${Intl.NumberFormat('en').format(value)}`,
            iconColor: '#14ABE0',
            bgColor: '#73D8FC',
        };
    });

    mappedData.pop();

    return mappedData.map((item) => ({
        ...item,
        date: item.label,
        label: 'Contacts',
    }));
};

const loadData = async () => {
    const params = {
        range: computedRange.value,
    };

    const context = {
        teamId,
        orgUnitId: props.orgUnitId,
        groupId: props.groupId,
    };

    const contactsTotalCreated = await analyticsClient.getContactsTotalCreated(context, params, props.type);
    const marketingSubscriptionsTotalOptIns = await analyticsClient.getMarketingSubscriptionsTotalOptIns(
        context,
        params,
        props.type
    );

    const marketingSubscriptionsTotalOptInsValues = marketingSubscriptionsTotalOptIns.data.values;
    const marketingSubscriptionsTotalOptInsTimeRange = computedRange.value.map((date, index) => {
        const value = marketingSubscriptionsTotalOptInsValues[index] || 0;
        return { label: 'Subscribers', date: new Date(date), count: value };
    });
    marketingSubscriptionsTotalOptInsTimeRange.pop();

    const marketingSubscriptionsTotalOptOuts = await analyticsClient.getMarketingSubscriptionsTotalOptOuts(
        context,
        params,
        props.type
    );

    const marketingSubscriptionsTotalOptOutsValues = marketingSubscriptionsTotalOptOuts.data.values;
    const marketingSubscriptionsTotalOptOutsTimeRange = computedRange.value.map((date, index) => {
        const value = marketingSubscriptionsTotalOptOutsValues[index] || 0;
        return { label: 'Unsubscribers', date: new Date(date), count: value };
    });
    marketingSubscriptionsTotalOptOutsTimeRange.pop();

    return [
        ...marketingSubscriptionsTotalOptInsTimeRange,
        ...mapContactsTotalCreated(contactsTotalCreated.data),
        ...marketingSubscriptionsTotalOptOutsTimeRange,
    ];
};

const dataComputed = computed(() => {
    const responseData = response.value || [];

    return {
        data:
            responseData.every((i) => i.count === 0) || [...new Set(responseData.map((item) => item.date))].length <= 1
                ? []
                : responseData,
        colors: responseData.length
            ? [
                  {
                      // blue/300/500
                      iconColor: '#3B82F6',
                      bgColor: '#93C5FD',
                      label: 'Contacts',
                  },
                  {
                      // sky/300/500
                      iconColor: '#14ABE0',
                      bgColor: '#73D8FC',
                      label: 'Subscribers',
                  },
                  {
                      // violet/300/500
                      iconColor: '#8B5CF6',
                      bgColor: '#C4B5FD',
                      label: 'Unsubscribers',
                  },
              ]
            : [],
    };
});

watch(
    [() => props.startDate, () => props.endDate],
    () => {
        chartError.value = false;

        fetchData(loadData()).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true }
);
</script>
